import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function FloorSetup() {
  const qc = useQueryClient();
  const [area, setArea] = useState({ name: "", tax_rate: 5 });
  const [term, setTerm] = useState({ name: "", station: "counter" });
  const { data: areas = [] } = useQuery({ queryKey: ["areas"], queryFn: async () => (await api.get("/areas")).data });
  const { data: terminals = [] } = useQuery({ queryKey: ["terminals"], queryFn: async () => (await api.get("/terminals")).data });
  const { data: tables = [] } = useQuery({ queryKey: ["tables"], queryFn: async () => (await api.get("/tables")).data });

  const addArea = async () => {
    try {
      await api.post("/areas", area);
      toast.success("Area added");
      setArea({ name: "", tax_rate: 5 });
      qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addTerm = async () => {
    try {
      await api.post("/terminals", term);
      toast.success("Terminal added");
      setTerm({ name: "", station: "counter" });
      qc.invalidateQueries({ queryKey: ["terminals"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const linkTable = async (tableId, areaId) => {
    try {
      await api.put(`/tables/${tableId}/area`, { area_id: areaId });
      toast.success("Table linked");
      qc.invalidateQueries({ queryKey: ["tables"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteArea = async (id) => {
    if (!window.confirm("Delete this area?")) return;
    try {
      await api.delete(`/areas/${id}`);
      toast.success("Area deleted");
      qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteTerminal = async (id) => {
    if (!window.confirm("Delete this terminal?")) return;
    try {
      await api.delete(`/terminals/${id}`);
      toast.success("Terminal deleted");
      qc.invalidateQueries({ queryKey: ["terminals"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };


  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="floor-setup">
      <PageHeader title="Areas & terminals" subtitle="Area-wise tax/menus and multi-terminal billing counters" />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="kul-card p-5 space-y-3">
          <h3 className="font-display font-700">Dining areas</h3>
          <div><Label>Name</Label><Input value={area.name} onChange={(e) => setArea({ ...area, name: e.target.value })} placeholder="Rooftop" /></div>
          <div><Label>Tax %</Label><Input type="number" value={area.tax_rate} onChange={(e) => setArea({ ...area, tax_rate: Number(e.target.value) })} /></div>
          <Button onClick={addArea} className="bg-[var(--kul-primary)] text-white">Add area</Button>
          <div className="space-y-2 pt-2">
            {areas.map((a) => <div key={a.id} className="text-sm flex justify-between border-b py-2 gap-2"><span>{a.name}</span><span className="flex items-center gap-2"><span>{a.tax_rate}% tax</span><button type="button" className="text-xs text-[var(--kul-danger)]" onClick={() => deleteArea(a.id)}>Delete</button></span></div>)}
          </div>
          <h4 className="font-medium pt-4">Link tables</h4>
          {tables.map((t) => (
            <div key={t.id} className="flex items-center gap-2 text-sm">
              <span className="w-20">{t.name}</span>
              <select className="h-9 border rounded px-2 flex-1" value={t.area_id || ""} onChange={(e) => linkTable(t.id, e.target.value)}>
                <option value="">No area</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="kul-card p-5 space-y-3">
          <h3 className="font-display font-700">Billing terminals</h3>
          <div><Label>Name</Label><Input value={term.name} onChange={(e) => setTerm({ ...term, name: e.target.value })} placeholder="Counter 2" /></div>
          <div><Label>Station</Label><Input value={term.station} onChange={(e) => setTerm({ ...term, station: e.target.value })} /></div>
          <Button onClick={addTerm} className="bg-[var(--kul-primary)] text-white">Add terminal</Button>
          <div className="space-y-2 pt-2">
            {terminals.map((t) => (
              <div key={t.id} className="text-sm flex justify-between border-b py-2 gap-2">
                <span>{t.name} · {t.code}</span>
                <span className="flex items-center gap-2">
                  <span className="text-neutral-400">{t.station}</span>
                  <button type="button" className="text-xs text-[var(--kul-danger)]" onClick={() => deleteTerminal(t.id)}>Delete</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
